// src/utils/storage.js — IndexedDB recording/session/audio stores

// ============================================================================
// INDEXEDDB RECORDING STORE (Legacy v1 — kept for migration)
// ============================================================================
export const RecordingStore = (() => {
    const DB_NAME = 'dentscribe-recordings';
    const STORE_NAME = 'pending-uploads';
    const DB_VERSION = 1;

    function open() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async function getAll() {
        try {
            const db = await open();
            return new Promise((resolve) => {
                const tx = db.transaction(STORE_NAME, 'readonly');
                const store = tx.objectStore(STORE_NAME);
                const req = store.getAll();
                req.onsuccess = () => { db.close(); resolve(req.result || []); };
                req.onerror = () => { db.close(); resolve([]); };
            });
        } catch { return []; }
    }

    async function remove(id) {
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.delete(id);
            req.onsuccess = () => { db.close(); resolve(); };
            req.onerror = () => { db.close(); reject(req.error); };
        });
    }

    async function clear() {
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.clear();
            req.onsuccess = () => { db.close(); resolve(); };
            req.onerror = () => { db.close(); reject(req.error); };
        });
    }

    return { open, getAll, remove, clear };
})();

// ============================================================================
// INDEXEDDB SESSION STORE v2 (Crash-proof recording sessions)
// ============================================================================
export const SessionStore = (() => {
    const DB_NAME = 'dentscribe-sessions';
    const STORE_NAME = 'recording-sessions';
    const DB_VERSION = 1;

    function open() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('sessionId', 'sessionId', { unique: true });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async function create(sessionData) {
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.add(sessionData);
            req.onsuccess = () => { db.close(); resolve(req.result); };
            req.onerror = () => { db.close(); reject(req.error); };
        });
    }

    async function update(id, updates) {
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const getReq = store.get(id);
            getReq.onsuccess = () => {
                const record = getReq.result;
                if (!record) { db.close(); reject(new Error('Session not found')); return; }
                Object.assign(record, updates);
                const putReq = store.put(record);
                putReq.onsuccess = () => { db.close(); resolve(record); };
                putReq.onerror = () => { db.close(); reject(putReq.error); };
            };
            getReq.onerror = () => { db.close(); reject(getReq.error); };
        });
    }

    async function get(id) {
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const req = store.get(id);
            req.onsuccess = () => { db.close(); resolve(req.result || null); };
            req.onerror = () => { db.close(); reject(req.error); };
        });
    }

    async function getAll() {
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const req = store.getAll();
            req.onsuccess = () => { db.close(); resolve(req.result || []); };
            req.onerror = () => { db.close(); resolve([]); };
        });
    }

    async function remove(id) {
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.delete(id);
            req.onsuccess = () => { db.close(); resolve(); };
            req.onerror = () => { db.close(); reject(req.error); };
        });
    }

    async function appendSegment(id, segmentBlob, totalDurationMs) {
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const getReq = store.get(id);
            getReq.onsuccess = () => {
                const record = getReq.result;
                if (!record) { db.close(); reject(new Error('Session not found')); return; }
                record.segments.push(segmentBlob);
                record.lastSavedAt = new Date().toISOString();
                record.totalDurationMs = totalDurationMs;
                const putReq = store.put(record);
                putReq.onsuccess = () => { db.close(); resolve(record); };
                putReq.onerror = () => { db.close(); reject(putReq.error); };
            };
            getReq.onerror = () => { db.close(); reject(getReq.error); };
        });
    }

    async function cleanOld() {
        try {
            const all = await getAll();
            const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
            for (const session of all) {
                if (session.status === 'completed') {
                    const ts = new Date(session.lastSavedAt || session.startedAt).getTime();
                    if (ts < sevenDaysAgo) await remove(session.id);
                }
            }
        } catch (err) { /* non-fatal */ }
    }

    return { open, create, update, get, getAll, remove, appendSegment, cleanOld };
})();

// ============================================================================
// AUDIO BLOB CACHE — persists downloaded S3 audio locally by noteId
// ============================================================================
export const AudioCache = (() => {
    const DB_NAME = 'dentscribe-audio-cache';
    const STORE_NAME = 'blobs';
    const DB_VERSION = 1;

    function open() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'noteId' });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async function get(noteId) {
        try {
            const db = await open();
            return new Promise((resolve) => {
                const tx = db.transaction(STORE_NAME, 'readonly');
                const store = tx.objectStore(STORE_NAME);
                const req = store.get(noteId);
                req.onsuccess = () => { db.close(); resolve(req.result || null); };
                req.onerror = () => { db.close(); resolve(null); };
            });
        } catch (e) { return null; }
    }

    async function put(noteId, blob) {
        try {
            const db = await open();
            return new Promise((resolve) => {
                const tx = db.transaction(STORE_NAME, 'readwrite');
                const store = tx.objectStore(STORE_NAME);
                store.put({ noteId, blob, cachedAt: Date.now() });
                tx.oncomplete = () => { db.close(); resolve(); };
                tx.onerror = () => { db.close(); resolve(); };
            });
        } catch (e) { /* non-fatal */ }
    }

    async function clear() {
        try {
            const db = await open();
            return new Promise((resolve) => {
                const tx = db.transaction(STORE_NAME, 'readwrite');
                tx.objectStore(STORE_NAME).clear();
                tx.oncomplete = () => { db.close(); resolve(); };
                tx.onerror = () => { db.close(); resolve(); };
            });
        } catch (e) { /* non-fatal */ }
    }

    return { get, put, clear };
})();
